import { Box, Divider } from "@mui/material"
import type { FC } from "react"
import { Fragment, useMemo } from "react"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"

import { SalesOrderFreightLine } from "./SalesOrderFreightLine"
import { SalesOrderLine } from "./SalesOrderLine"
import { SalesOrderTotals } from "./SalesOrderTotals"

import type { SalesOrder } from "@/graphql/codegen/graphql"
import { useCalculateSalesOrderPricingTotals } from "@/screens/CustomerOrders/hooks/useCalculateSalesOrderPricingTotals"
import type { SalesOrderLogisticsFormValues } from "@/screens/CustomerOrders/schemas"
import { isSalesOrderEditable } from "@/screens/CustomerOrders/utils"

interface SalesOrderFormLinesProps {
  salesOrder: SalesOrder
}

export const SalesOrderFormLines: FC<SalesOrderFormLinesProps> = ({ salesOrder }) => {
  const { control } = useFormContext<SalesOrderLogisticsFormValues>()
  const { fields } = useFieldArray({ control, name: "lines" })

  const [incoterms, incotermsLocationId, freightTerms, shipFromWarehouseAreaId, lines, freightLine] = useWatch({
    control,
    name: ["incoterms", "incotermsLocationId", "freightTerms", "shipFromWarehouseAreaId", "lines", "freightLine"],
  })
  const productIds = useMemo(() => salesOrder.lines.map((line) => line.product.productId), [salesOrder.lines])

  const {
    lines: lineTotals,
    total: pricingTotal,
    isFetching: isPricingFetching,
  } = useCalculateSalesOrderPricingTotals({
    customerId: salesOrder.lines[0]?.customerPurchaseOrder.companyId ?? "",
    productIds,
    header: { incoterms, incotermsLocationId, freightTerms, shipFromWarehouseAreaId },
    lines,
    freightLine,
  })

  return (
    <Box className='flex flex-col gap-4'>
      {fields.map((field, index) => (
        <Fragment key={field.id}>
          {index > 0 && <Divider />}
          <SalesOrderLine
            index={index}
            cpoNumber={salesOrder.lines[index].customerPurchaseOrder.cpoNumber}
            product={salesOrder.lines[index].product}
            lineTotals={lineTotals?.[index]}
            isPricingFetching={isPricingFetching}
            readOnly={!isSalesOrderEditable(salesOrder.status)}
          />
        </Fragment>
      ))}
      <SalesOrderFreightLine readOnly={!isSalesOrderEditable(salesOrder.status)} />
      <SalesOrderTotals salesOrder={salesOrder} pricingTotal={pricingTotal} isPricingFetching={isPricingFetching} />
    </Box>
  )
}
